import { createContext, useContext } from 'react';

import { useGuardedContext } from '@/hooks/useGuardedContext';
import { EntityDocumentViewer_FilePageFragment } from '@/modules/documents/components/HouseholdDocumentsViewer/graphql/EntityDocumentViewer.generated';
import { DocumentDetails_DocumentSummaryFragment } from '@/modules/documents/graphql/DocumentDetails.generated';
import { SelectableDocument } from '@/modules/documents/ManageDocumentAmendments/types';
import type { PDFSource } from '@/modules/pdf/PDFViewer';

export interface DocumentDetailsContextValue {
  // setIsGeneratingAiSummaryOverride is used to override the isGeneratingAiSummary state so that we
  // can give instant feedback to the user that the AI summary is being generated.
  // We use it in addition to workflow tracker behavior because the workflow tracker
  // can take up to half a second to register as active.
  setIsGeneratingAiSummaryOverride: (
    isGeneratingAiSummaryOverride: boolean
  ) => void;
  isEditSummaryTrowserOpen: boolean;
  openEditSummaryTrowser: () => void;
  closeEditSummaryTrowser: () => void;
  // The currently selected/active summary (derived from summaries + selectedSummaryId)
  documentSummary?: DocumentDetails_DocumentSummaryFragment | null;
  // All available summaries for this document
  summaries: DocumentDetails_DocumentSummaryFragment[];
  documentFilePages: EntityDocumentViewer_FilePageFragment[];
  documentId: string;
  source: PDFSource | null;
  associatedDocuments: SelectableDocument[];
  selectedSummaryId: string | null;
  setSelectedSummaryId: (summaryId: string) => void;
  // transitionToNewSummaryWhenAvailable is used to prepare the UI to automatically
  // select the new summary when it is available.
  transitionToNewSummaryWhenAvailable: boolean;
  setTransitionToNewSummaryWhenAvailable: (value: boolean) => void;
}

export const DocumentDetailsContext =
  createContext<DocumentDetailsContextValue | null>(null);

export function useDocumentDetails() {
  return useGuardedContext(DocumentDetailsContext, 'DocumentDetailsProvider');
}

export function useUnguardedDocumentDetails() {
  return useContext(DocumentDetailsContext);
}
